import React, { useState, useEffect } from 'react';
import * as PR from "../../prime-modules/index";
import { getUserByEmail, createEndUser, createOnboardingOrder, getSubscriptionPlans } from "../../services/api";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

function NewCustomerModal({ visible, onHide, toast, headers, dispatch, refreshData }) {
    const [onboardEmail, setOnboardEmail] = useState("");
    const [onboardUserChecked, setOnboardUserChecked] = useState(false);
    const [onboardUserExists, setOnboardUserExists] = useState(false);
    const [onboardUserId, setOnboardUserId] = useState("");
    const [onboardFirstName, setOnboardFirstName] = useState("");
    const [onboardLastName, setOnboardLastName] = useState("");
    const [onboardCallingCode, setOnboardCallingCode] = useState("");
    const [onboardLocalPhone, setOnboardLocalPhone] = useState("");
    const [onboardPlans, setOnboardPlans] = useState([]);
    const [onboardSelectedPlan, setOnboardSelectedPlan] = useState(null);
    const [onboardWantsPhysicalSim, setOnboardWantsPhysicalSim] = useState(false);
    const [onboardLoading, setOnboardLoading] = useState(false);
    const [checkingEmail, setCheckingEmail] = useState(false);

    useEffect(() => {
        if (visible) {
            getSubscriptionPlans(headers, dispatch, (response) => {
                if (response.result === 'SUCCESS') {
                    setOnboardPlans(response.data || []);
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch subscription plans.' });
                }
            });
        }
    }, [visible, headers, dispatch, toast]);

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(String(email).toLowerCase());
    };

    const removeLeadingZero = (value) => value.replace(/^0+/, "");

    const getFullPhone = () => {
        if (!onboardCallingCode) return onboardLocalPhone || "";
        const prefix = onboardCallingCode.startsWith('+') ? '' : '+';
        return `${prefix}${onboardCallingCode}${onboardLocalPhone || ''}`;
    };

    const checkUserEmail = (emailToCheck) => {
        if (!emailToCheck || !validateEmail(emailToCheck)) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Please enter a valid email address.' });
            return;
        }
        setCheckingEmail(true);
        getUserByEmail(emailToCheck, headers, dispatch, (response) => {
            setCheckingEmail(false);
            setOnboardUserChecked(true);
            if (response.result === 'SUCCESS') {
                const userData = response.data;
                setOnboardUserExists(true);
                setOnboardUserId(userData.userId);
                setOnboardFirstName(userData.firstName || "");
                setOnboardLastName(userData.lastName || "");
                setOnboardCallingCode(userData.phone?.callingCode || "");
                setOnboardLocalPhone(userData.phone?.localPhoneNumber || "");
                setOnboardSelectedPlan(null);
                toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Existing user found.' });
            } else if (response.result === 'NOT_FOUND') {
                setOnboardUserExists(false);
                setOnboardUserId("");
                setOnboardFirstName("");
                setOnboardLastName("");
                setOnboardCallingCode("+44");
                setOnboardLocalPhone("");
                setOnboardSelectedPlan(null);
                toast.current?.show({ severity: 'info', summary: 'Info', detail: 'User not found. Please fill in details for new user.' });
            } else {
                setOnboardUserChecked(false);
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to verify user email. Please try again.' });
            }
        });
    };

    const handleOnboardClose = () => {
        setOnboardEmail("");
        setOnboardUserChecked(false);
        setOnboardUserExists(false);
        setOnboardUserId("");
        setOnboardFirstName("");
        setOnboardLastName("");
        setOnboardCallingCode("");
        setOnboardLocalPhone("");
        setOnboardSelectedPlan(null);
        setOnboardWantsPhysicalSim(false);
        setOnboardLoading(false);
        onHide();
    };

    const handleOnboardSubmit = () => {
        if (!onboardEmail || !validateEmail(onboardEmail)) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Please enter a valid email address.' });
            return;
        }
        if (!onboardUserChecked) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Please check the email first.' });
            return;
        }
        if (!onboardSelectedPlan) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Please select a subscription plan.' });
            return;
        }

        if (!onboardUserExists) {
            if (!onboardFirstName.trim() || !onboardLastName.trim()) {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'First name and Last name are required.' });
                return;
            }
          
        }

        setOnboardLoading(true);

        const proceedWithOrder = (userId) => {
            const orderData = {
                planName: onboardSelectedPlan,
                userId: userId,
                wantsPhysicalSim: onboardWantsPhysicalSim
            };

            createOnboardingOrder(orderData, headers, dispatch, (orderResponse) => {
                setOnboardLoading(false);
                if (orderResponse.result === 'SUCCESS') {
                    toast.current?.show({ severity: 'success', summary: 'Success', detail: 'New customer order created successfully.' });
                    handleOnboardClose();
                    refreshData();
                } else {
                    const error = orderResponse.error;
                    toast.current?.show({
                        severity: error?.severity || 'error',
                        summary: 'Error',
                        detail: error?.errorMsg || 'Failed to create new customer order.'
                    });
                }
            });
        };

        if (!onboardUserExists) {
            const userData = {
                email: onboardEmail,
                firstName: onboardFirstName,
                lastName: onboardLastName,
                phone: {
                    callingCode: onboardCallingCode || "+44",
                    localPhoneNumber: onboardLocalPhone
                }
            };

            createEndUser(userData, headers, dispatch, (userResponse) => {
                if (userResponse.result === 'SUCCESS') {
                    const user = userResponse.data;
                    proceedWithOrder(user?.userId);
                } else {
                    setOnboardLoading(false);
                    const error = userResponse.error;
                    toast.current?.show({
                        severity: error?.severity || 'error',
                        summary: 'Error',
                        detail: error?.errorMsg || 'Failed to create end-user.'
                    });
                }
            });
        } else {
            proceedWithOrder(onboardUserId);
        }
    };

    return (
        <PR.Dialog
            visible={visible}
            dismissableMask={true}
            draggable={false}
            resizable={false}
            header="New Customer"
            onHide={handleOnboardClose}
            className="affiliate-dialog"
            maskClassName="affiliate-dialog-mask"
            style={{ width: '90%', maxWidth: '600px' }}
        >
            <div style={{ marginTop: '1rem' }} className="onboard-dialog-content">
                <div className="flex flex-column gap-2 mb-3">
                    <p className="m-0 font-semibold text-sm">Email ID <span className="validation-required">*</span></p>
                    <div className="flex gap-2">
                        <PR.InputText 
                            id="onboardEmail"
                            value={onboardEmail}
                            onChange={(e) => {
                                setOnboardEmail(e.target.value);
                                setOnboardUserChecked(false);
                                setOnboardUserExists(false);
                            }}
                            onBlur={(e) => {
                                if (e.target.value && validateEmail(e.target.value) && !onboardUserChecked) {
                                    checkUserEmail(e.target.value);
                                }
                            }}
                            placeholder="Enter user email"
                            className="flex-1"
                            disabled={checkingEmail || onboardLoading}
                        />
                        <PR.Button 
                            label={checkingEmail ? "" : "Check"} 
                            icon={checkingEmail ? "pi pi-spin pi-spinner" : "pi pi-search"}
                            onClick={() => checkUserEmail(onboardEmail)}
                            disabled={checkingEmail || onboardLoading || !onboardEmail}
                            className="confirm-button"
                            style={{ minWidth: '90px' }}
                        />
                    </div>
                </div>

                {onboardUserChecked && (
                    <>
                        {onboardUserExists ? (
                            <div className="p-2 mb-3 border-round bg-green-50 border-1 border-green-200" style={{ backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: '6px', padding: '10px' }}>
                                <p className="m-0 text-green-700 text-sm flex align-items-center gap-2" style={{ color: '#2e7d32', margin: 0 }}>
                                    <i className="pi pi-check-circle"></i> User exists. Details pre-populated.
                                </p>
                            </div>
                        ) : (
                            <div className="p-2 mb-3 border-round bg-blue-50 border-1 border-blue-200" style={{ backgroundColor: '#e3f2fd', border: '1px solid #bbdefb', borderRadius: '6px', padding: '10px' }}>
                                <p className="m-0 text-blue-700 text-sm flex align-items-center gap-2" style={{ color: '#1565c0', margin: 0 }}>
                                    <i className="pi pi-info-circle"></i> User does not exist. Please fill in details for new user.
                                </p>
                            </div>
                        )}

                        <div className="grid">
                            <div className="col-12 md:col-6 flex flex-column gap-2 mb-3">
                                <p className="mb-2">First Name <span className="validation-required">*</span></p>
                                <PR.InputText 
                                    id="onboardFirstName"
                                    value={onboardFirstName}
                                    onChange={(e) => setOnboardFirstName(e.target.value)}
                                    placeholder="First Name"
                                    className="w-full"
                                    disabled={onboardUserExists || onboardLoading}
                                />
                            </div>
                            <div className="col-12 md:col-6 flex flex-column gap-2 mb-3">
                                <p className="mb-2">Last Name <span className="validation-required">*</span></p>
                                <PR.InputText 
                                    id="onboardLastName"
                                    value={onboardLastName}
                                    onChange={(e) => setOnboardLastName(e.target.value)}
                                    placeholder="Last Name"
                                    className="w-full"
                                    disabled={onboardUserExists || onboardLoading}
                                />
                            </div>
                        </div>

                        <div className="flex flex-column gap-2 mb-3">
                            <p className="mb-2">Phone Number </p>
                            <div className="w-full">
                                <PhoneInput
                                    inputProps={{
                                        name: "onboardPhone",
                                        required: true,
                                        id: "onboard-phone-input",
                                    }}
                                    countryCodeEditable={false}
                                    autoFormat={true}
                                    enableSearch={true}
                                    searchPlaceholder="Search"
                                    placeholder="Enter Phone number"
                                    country="gb"
                                    value={getFullPhone()}
                                    onChange={(value, data) => {
                                        let phoneValue = `+${value}`.replace(`+${data.dialCode}`, "");
                                        phoneValue = removeLeadingZero(phoneValue);
                                        setOnboardLocalPhone(phoneValue);
                                        setOnboardCallingCode(`+${data.dialCode}`);
                                    }}
                                    disabled={onboardUserExists || onboardLoading}
                                    style={{ width: "100%" }}
                                />
                            </div>
                        </div>

                        <div className="grid mt-2">
                            <div className="col-12 flex flex-column gap-2 mb-3">
                                <p className="mb-2">Subscription Plan <span className="validation-required">*</span></p>
                                <PR.Dropdown 
                                    id="onboardPlan"
                                    value={onboardSelectedPlan}
                                    options={onboardPlans.map(plan => ({ label: plan.description, value: plan.name }))}
                                    onChange={(e) => setOnboardSelectedPlan(e.value)}
                                    placeholder="Select Plan"
                                    className="w-full"
                                    disabled={onboardLoading}
                                    filter
                                />
                            </div>
                        </div>

                        <div className="flex align-items-center justify-content-between mb-4 mt-2" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label htmlFor="onboardSim" style={{ fontSize: '16px', fontWeight: 'bold' }}>Wants Physical SIM</label>
                            <PR.InputSwitch 
                                id="onboardSim"
                                checked={onboardWantsPhysicalSim}
                                onChange={(e) => setOnboardWantsPhysicalSim(e.value)}
                                disabled={onboardLoading}
                            />
                        </div>
                    </>
                )}

                <div className="dialog-footer buttons-sections flex gap-3 align-items-center justify-content-end mt-5" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <PR.Button 
                        label="Cancel" 
                        type="button"
                        onClick={handleOnboardClose} 
                        className="confirm-button reset-btn min-width"
                        disabled={onboardLoading}
                    />
                    <PR.Button 
                        label={onboardLoading ? "Processing..." : "Submit"} 
                        icon={onboardLoading ? "pi pi-spin pi-spinner" : "pi pi-check"}
                        type="button"
                        onClick={handleOnboardSubmit} 
                        className="confirm-button min-width"
                        disabled={onboardLoading || !onboardUserChecked || !onboardFirstName || !onboardLastName || !onboardSelectedPlan}
                    />
                </div>
            </div>
        </PR.Dialog>
    );
}

export default NewCustomerModal;
